import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Box,
  useMediaQuery,
  useTheme,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useHistory } from 'react-router-dom';

const AdminDramas = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const history = useHistory();
  const [dramas, setDramas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedDrama, setSelectedDrama] = useState(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newDrama, setNewDrama] = useState({
    name: '',
    description: '',
    category: '悬疑',
    poster: null,
    posterPreview: '',
    duration: 0,
  });

  // 处理海报上传
  const handlePosterUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setNewDrama(prev => ({
          ...prev,
          poster: file,
          posterPreview: reader.result,
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  useEffect(() => {
    fetchDramas();
  }, []);

  // 从 API 获取剧集列表
  const fetchDramas = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/dramas', {
        headers: {
          'Content-Type': 'application/json',
          // 可以添加身份验证头部
        },
      });

      if (!response.ok) {
        throw new Error('获取剧集列表失败');
      }

      const result = await response.json();
      if (result.success && Array.isArray(result.data)) {
        setDramas(result.data);
      } else {
        setDramas([]);
      }
    } catch (error) {
      console.error('获取剧集列表失败:', error);
      setDramas([]);
    } finally {
      setLoading(false);
    }
  };

  // 处理编辑操作
  const handleEdit = (drama) => {
    history.push(`/admin/edit/${drama.id}`);
  };

  // 处理删除操作
  const handleDeleteClick = (drama) => {
    setSelectedDrama(drama);
    setDeleteDialogOpen(true);
  };

  // 确认删除
  const handleDeleteConfirm = async () => {
    if (!selectedDrama) return;

    try {
      const response = await fetch(`/api/dramas/${selectedDrama.id}`, {
        method: 'DELETE',
        credentials: 'include', // 添加跨域请求时包含凭证
      });

      if (response.ok) {
        // 删除成功后刷新列表
        await fetchDramas();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || '删除失败');
      }
    } catch (error) {
      console.error('删除剧集失败:', error);
      alert('删除剧集失败: ' + error.message);
    }

    setDeleteDialogOpen(false);
    setSelectedDrama(null);
  };

  // 取消删除
  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSelectedDrama(null);
  };

  // 处理添加剧集
  const handleAddDrama = () => {
    setAddDialogOpen(true);
  };

  // 提交新剧集
  const handleAddSubmit = async () => {
    try {
      console.log('尝试添加剧集:', newDrama);
      
      // 创建 FormData 对象用于文件上传
      const formData = new FormData();
      formData.append('name', newDrama.name);
      formData.append('description', newDrama.description);
      formData.append('category', newDrama.category);
      formData.append('duration', newDrama.duration);
      
      if (newDrama.poster) {
        formData.append('poster', newDrama.poster);
      }

      const response = await fetch('/api/dramas', {
        method: 'POST',
        body: formData,
        credentials: 'include', // 添加跨域请求时包含凭证
      });

      if (response.ok) {
        await fetchDramas();
        setAddDialogOpen(false);
        setNewDrama({
          name: '',
          description: '',
          category: '悬疑',
          poster: null,
          posterPreview: '',
          duration: 0,
        });
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || '添加剧集失败');
      }
    } catch (error) {
      console.error('添加剧集失败:', error);
      // 添加用户友好的错误提示
      alert('添加剧集失败: ' + error.message);
    }
  };

  // 取消添加
  const handleAddCancel = () => {
    setAddDialogOpen(false);
    setNewDrama({
      name: '',
      description: '',
      category: '悬疑',
      poster: null,
      posterPreview: '',
      duration: 0,
    });
  };

  // 更新新剧信息
  const handleNewDramaChange = (field, value) => {
    setNewDrama(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  // 渲染加载状态
  if (loading) {
    return (
      <Box sx={{ flexGrow: 1 }}>
        <Container maxWidth="lg" sx={{ 
          mt: 3, 
          mb: 4, 
          px: isMobile ? 1 : 2,
        }}>
          <Typography variant="h6">加载中...</Typography>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Container maxWidth="lg" sx={{ 
        mt: 3, 
        mb: 4, 
        px: isMobile ? 1 : 2,
      }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mb: 3 
        }}>
          <Typography 
            variant={isMobile ? "h6" : "h5"} 
            component="h2" 
            sx={{ fontSize: isMobile ? '1.25rem' : '1.5rem' }}
          >
            剧集管理
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddDrama}
            sx={{ height: 40 }}
          >
            添加剧集
          </Button>
        </Box>

        <Paper sx={{ 
          p: isMobile ? 1 : 2, 
          borderRadius: 2,
          overflowX: 'auto'
        }}>
          <TableContainer sx={{ minWidth: 650 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>剧集名称</TableCell>
                  <TableCell>分类</TableCell>
                  <TableCell align="center">播放次数</TableCell>
                  <TableCell align="center">收藏次数</TableCell>
                  <TableCell align="center">操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {dramas.length > 0 ? (
                  dramas.map((drama) => (
                    <TableRow key={drama.id}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {drama.poster && (
                            <img
                              src={drama.poster.startsWith('/') ? drama.poster : '/' + drama.poster}
                              alt={drama.name}
                              style={{
                                width: 40,
                                height: 40,
                                objectFit: 'cover',
                                borderRadius: 4,
                              }}
                            />
                          )}
                          <span>{drama.name}</span>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={drama.category || '未分类'}
                          size="small"
                          color="primary"
                          sx={{ fontSize: '0.75rem' }}
                        />
                      </TableCell>
                      <TableCell align="center">{drama.view_count || 0}</TableCell>
                      <TableCell align="center">{drama.favorite_count || 0}</TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(drama)}
                            sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteClick(drama)}
                            sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      暂无剧集
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* 删除确认对话框 */}
        <Dialog
          open={deleteDialogOpen}
          onClose={handleDeleteCancel}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>删除剧集</DialogTitle>
          <DialogContent>
            <Typography>
              您确定要删除剧集 <strong>{selectedDrama?.name}</strong> 吗？此操作无法撤销。
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteCancel}>取消</Button>
            <Button onClick={handleDeleteConfirm} color="error">
              删除
            </Button>
          </DialogActions>
        </Dialog>

        {/* 添加剧集对话框 */}
        <Dialog
          open={addDialogOpen}
          onClose={handleAddCancel}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>添加新剧集</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="剧集名称"
                  value={newDrama.name}
                  onChange={(e) => handleNewDramaChange('name', e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="剧集描述"
                  multiline
                  rows={3}
                  value={newDrama.description}
                  onChange={(e) => handleNewDramaChange('description', e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>分类</InputLabel>
                  <Select
                    value={newDrama.category}
                    onChange={(e) => handleNewDramaChange('category', e.target.value)}
                    label="分类"
                  >
                    <MenuItem value="悬疑">悬疑</MenuItem>
                    <MenuItem value="爱情">爱情</MenuItem>
                    <MenuItem value="喜剧">喜剧</MenuItem>
                    <MenuItem value="科幻">科幻</MenuItem>
                    <MenuItem value="恐怖">恐怖</MenuItem>
                    <MenuItem value="动作">动作</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    海报图片
                  </Typography>
                  {newDrama.posterPreview && (
                    <Box sx={{ mb: 2 }}>
                      <img 
                        src={newDrama.posterPreview} 
                        alt="Poster Preview" 
                        style={{ 
                          width: '100%', 
                          maxWidth: 300, 
                          height: 'auto', 
                          objectFit: 'cover', 
                          borderRadius: 8 
                        }}
                      />
                    </Box>
                  )}
                  <Button
                    variant="outlined"
                    component="label"
                    fullWidth
                    sx={{ 
                      height: 48,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {newDrama.poster ? '更换海报' : '选择海报'}
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={handlePosterUpload}
                    />
                  </Button>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="时长 (分钟)"
                  type="number"
                  value={newDrama.duration || 0}
                  onChange={(e) => handleNewDramaChange('duration', parseInt(e.target.value) || 0)}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleAddCancel}>取消</Button>
            <Button onClick={handleAddSubmit} variant="contained">
              添加
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default AdminDramas;